import { useTranslation } from "react-i18next";
import { getLocationLocale, formatLocationRelativeTime } from "../../utils/locationLocale";
import {
	Fragment,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import type { ILocationProps } from "../../services/Location/types";
import {
	Circle,
	CircleMarker,
	MapContainer,
	Marker,
	Popup,
	TileLayer,
	useMap,
	useMapEvents,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import DriftMarker from "react-leaflet-drift-marker";
import { useQuery } from "react-query";
import { Avatar, Box, IconButton, Tooltip as MuiTooltip } from "@mui/material";
import SettingsInputAntennaIcon from "@mui/icons-material/SettingsInputAntenna";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import StopIcon from "@mui/icons-material/Stop";
import vanIcon from "../../assets/images/icons/map/vanIcon.webp";
const defaultIcon = L.icon({
	iconUrl: vanIcon,
	iconRetinaUrl: vanIcon,
	iconSize: [40, 41],
	iconAnchor: [20, 41],
	popupAnchor: [0, -34],
	shadowSize: [41, 41],
});
const mobileDriverOnlineIcon = L.icon({
	iconUrl: vanIcon,
	iconRetinaUrl: vanIcon,
	iconSize: [40, 41],
	iconAnchor: [20, 41],
	popupAnchor: [0, -34],
	shadowSize: [41, 41],
	className: "vehicle-marker-mobile-online",
});
const vehicleOfflineIcon = L.icon({
	iconUrl: vanIcon,
	iconRetinaUrl: vanIcon,
	iconSize: [40, 41],
	iconAnchor: [20, 41],
	popupAnchor: [0, -34],
	shadowSize: [41, 41],
	className: "vehicle-marker-offline",
});
import { t } from "i18next";
import L from "leaflet";
import { useLocations } from "../../pages/LocationPage/_components/LocationContext";
import type {
	GeofenceCenter,
	Geofence,
} from "../../pages/LocationPage/_components/geofenceUtils";
import { isPointInsideGeofence } from "../../pages/LocationPage/_components/geofenceUtils";
import { AppContext } from "../../context/AppContextProvider";
import { getActiveSchoolTransportationAttendanceAsync } from "../../services/SchoolTransportation";
import { Permissions } from "../../services/enums";
import {
	SCHOOL_TRANSPORTATION_KEYS,
	useGetAllSchoolTransportationByLicenseQuery,
} from "../../services/SchoolTransportation/hooks/useQuery";
import {
	getLocationTelemetryStatus,
	getOperationalStatus,
	getTelemetryPresentation,
} from "../../pages/LocationPage/_components/locationStatus";
import { useLocationStatusClock } from "../../pages/LocationPage/_components/useLocationStatusClock";
import DriverInfoPopover from "../DriverInfoPopover";

interface MapViewProps {
	selectedId?: string | null;
	selectionRevision?: number;
	selectedIds: string[];
	onSelect: (id: string) => void;
	mapTheme?: MapTheme;
	onMapThemeChange?: (theme: MapTheme) => void;
	geofences?: Geofence[];
	geofencePanelOpen?: boolean;
	isPickingFenceCenter?: boolean;
	pickingGeofenceId?: string | null;
	focusedGeofenceId?: string | null;
	onFenceCenterSelected?: (id: string, center: GeofenceCenter) => void;
	onStopPickingFenceCenter?: () => void;
	onToggleGeofencePanel?: () => void;
	onStopFollow?: () => void;
}

export type MapTheme = "light" | "dark";

const MAP_THEME_STORAGE_KEY = "alertai:location-map-theme:v1";
const OPEN_STREET_MAP_TILE_URL =
	"https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
const OPEN_STREET_MAP_ATTRIBUTION =
	'&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

export function getStoredMapTheme(): MapTheme {
	if (typeof window === "undefined") {
		return "light";
	}

	try {
		return window.localStorage.getItem(MAP_THEME_STORAGE_KEY) === "dark"
			? "dark"
			: "light";
	} catch {
		return "light";
	}
}

type MappableLocation = ILocationProps & {
	latitude: number;
	longitude: number;
};

function escapeHtml(value: string): string {
	return value
		.replaceAll("&", "&amp;")
		.replaceAll("<", "&lt;")
		.replaceAll(">", "&gt;")
		.replaceAll('"', "&quot;")
		.replaceAll("'", "&#39;");
}

function createPlateIcon(plate: string): L.DivIcon {
	return L.divIcon({
		className: "vehicle-plate-marker",
		html: `
            <div class="vehicle-plate-marker__container">
                <span class="vehicle-plate-marker__text">${escapeHtml(plate)}</span>
            </div>
        `,
		iconSize: [90, 16],
		iconAnchor: [45, 8],
	});
}

function splitAddressAfterCity(address?: string) {
	const normalized = address?.trim();

	if (!normalized) {
		return {
			firstLine: t("locationTransport.addressNotFound"),
			secondLine: null as string | null,
		};
	}

	const citySeparatorIndex = normalized.indexOf(" - ");
	if (citySeparatorIndex < 0) {
		return { firstLine: normalized, secondLine: null as string | null };
	}

	return {
		firstLine: normalized.slice(0, citySeparatorIndex),
		secondLine: normalized.slice(citySeparatorIndex + 3),
	};
}

function formatPlateDisplay(plate?: string) {
	const normalized = plate?.trim();

	if (!normalized) {
		return "-";
	}

	const upperText = normalized.toUpperCase();
	const plateMatch = upperText.match(/([A-Z]{3})[\s-]?([A-Z0-9]{4})/);

	if (plateMatch) {
		return `${plateMatch[1]}-${plateMatch[2]}`;
	}

	const alphanumeric = upperText.replace(/[^A-Z0-9]/g, "");

	if (!alphanumeric) {
		return normalized;
	}

	const base = alphanumeric.slice(0, 7);
	return base.length <= 3 ? base : `${base.slice(0, 3)}-${base.slice(3)}`;
}

function normalizeVehiclePlate(plate?: string) {
	return plate?.replace(/[^a-zA-Z0-9]/g, "").toUpperCase() || "";
}

function formatLastLocationSignal(timestamp: string | null | undefined, now: number) {
  const time = formatLocationRelativeTime(timestamp, now);
  return time === null ? t("locationTransport.noSignalReceived") : t("locationTransport.lastSignalAgo", { time });
}

function hasCoordinates(
	location: ILocationProps,
): location is MappableLocation {
	return (
		location.hasLocation === true &&
		typeof location.latitude === "number" &&
		Number.isFinite(location.latitude) &&
		typeof location.longitude === "number" &&
		Number.isFinite(location.longitude)
	);
}

function getVehicleIcon(location: ILocationProps) {
	if (location.isOnline === false) {
		return vehicleOfflineIcon;
	}

	return location.source === "mobile-driver"
		? mobileDriverOnlineIcon
		: defaultIcon;
}

function MapController({
	selectedLocation,
	isFollowing,
}: {
	selectedLocation?: MappableLocation;
	isFollowing: boolean;
}) {
	const map = useMap();

	useEffect(() => {
		if (isFollowing && selectedLocation) {
			map.flyTo([selectedLocation.latitude, selectedLocation.longitude], 17, {
				animate: true,
				duration: 1,
			});
		}
	}, [isFollowing, map, selectedLocation]);

	return null;
}

function FocusedGeofenceController({
	geofence,
}: {
	geofence: Geofence | null;
}) {
	const map = useMap();

	useEffect(() => {
		if (!geofence?.center) {
			return;
		}

		map.flyTo([geofence.center.latitude, geofence.center.longitude], 17, {
			animate: true,
			duration: 0.8,
		});
	}, [geofence, map]);

	return null;
}

function ResizeController({ trigger }: { trigger?: boolean }) {
	const map = useMap();

	useEffect(() => {
		const timer = setTimeout(() => {
			// touch trigger so hook dependency is necessary
			if (typeof trigger !== "undefined") {
				map.invalidateSize();
				map.setView(map.getCenter(), map.getZoom(), {
					animate: true,
					duration: 0.5,
				});
			}
		}, 1);

		return () => clearTimeout(timer);
	}, [map, trigger]);

	return null;
}

function MapThemeController({ theme }: { theme: MapTheme }) {
	const map = useMap();

	useEffect(() => {
		const container = map.getContainer();
		container.classList.toggle("map-container--dark", theme === "dark");
		container.classList.toggle("map-container--light", theme === "light");
	}, [map, theme]);

	return null;
}

function MapClickController({
	isPickingFenceCenter,
	pickingGeofenceId,
	onFenceCenterSelected,
	onStopPickingFenceCenter,
}: {
	isPickingFenceCenter: boolean;
	pickingGeofenceId: string | null | undefined;
	onFenceCenterSelected: (id: string, center: GeofenceCenter) => void;
	onStopPickingFenceCenter: () => void;
}) {
	useMapEvents({
		click: (event) => {
			if (!isPickingFenceCenter || !pickingGeofenceId) {
				return;
			}

			onFenceCenterSelected(pickingGeofenceId, {
				latitude: event.latlng.lat,
				longitude: event.latlng.lng,
			});
			onStopPickingFenceCenter();
		},
	});

	return null;
}

function GeofenceOverlay({ geofence }: { geofence: Geofence }) {
	if (!geofence.enabled || !geofence.center) {
		return null;
	}

	return (
		<>
			<Circle
				center={[geofence.center.latitude, geofence.center.longitude]}
				radius={geofence.radiusMeters}
				pathOptions={{
					color: "#2563eb",
					weight: 2,
					fillColor: "#38bdf8",
					fillOpacity: 0.12,
				}}
			/>
			<CircleMarker
				center={[geofence.center.latitude, geofence.center.longitude]}
				radius={8}
				pathOptions={{
					color: "#0f172a",
					weight: 2,
					fillColor: "#f97316",
					fillOpacity: 1,
				}}
			/>
		</>
	);
}

function GeofenceNotificationController({
	geofence,
	locations,
}: {
	geofence: Geofence;
	locations: ILocationProps[];
}) {
  const { t } = useTranslation();
	const { showSnackbar } = useContext(AppContext);
	const previousStatusRef = useRef<Record<string, boolean>>({});
	const previousFenceKeyRef = useRef<string | null>(null);

	const fenceKey = geofence.center
		? `${geofence.id}-${geofence.enabled}-${geofence.center.latitude}-${geofence.center.longitude}-${geofence.radiusMeters}`
		: `${geofence.id}-${geofence.enabled}-empty-${geofence.radiusMeters}`;

	useEffect(() => {
		if (previousFenceKeyRef.current !== fenceKey) {
			previousFenceKeyRef.current = fenceKey;
			previousStatusRef.current = {};
		}
	}, [fenceKey]);

	useEffect(() => {
		if (!geofence.enabled || !geofence.center) {
			return;
		}

		const canUseBrowserNotification =
			typeof window !== "undefined" && "Notification" in window;

		const maybeSendBrowserNotification = async (
			title: string,
			body: string,
		) => {
			if (!canUseBrowserNotification) {
				return;
			}

			if (window.Notification.permission === "default") {
				try {
					await window.Notification.requestPermission();
				} catch {
					return;
				}
			}

			if (window.Notification.permission !== "granted") {
				return;
			}

			new window.Notification(title, { body });
		};

		for (const location of locations) {
			if (
				typeof location.latitude !== "number" ||
				typeof location.longitude !== "number"
			) {
				continue;
			}

			const currentInside = isPointInsideGeofence(
				location.latitude,
				location.longitude,
				geofence,
			);
			const previousInside = previousStatusRef.current[location.entityId];

			if (typeof previousInside === "undefined") {
				previousStatusRef.current[location.entityId] = currentInside;
				continue;
			}

			if (previousInside === currentInside) {
				continue;
			}

			previousStatusRef.current[location.entityId] = currentInside;

			const plate = location.vehiclePlate?.trim() || location.entityId;
			const fenceName = geofence.name?.trim() || t("locationTransport.fenceNumber", { index: geofence.id });
			const message = currentInside
				? `${t("map.vehicle")} ${plate} ${t("map.enteredFence")} "${fenceName}".`
				: `${t("map.vehicle")} ${plate} ${t("map.leftFence")} "${fenceName}".`;

			showSnackbar(message, currentInside ? "success" : "warning");
			void maybeSendBrowserNotification(
				`${t("map.virtualFence")} — ${fenceName}`,
				message,
			);
		}
	}, [geofence, locations, showSnackbar]);

	return null;
}

function InitialBoundsController({
	locations,
}: {
	locations: ILocationProps[];
}) {
	const map = useMap();
	const hasFitted = useRef(false);

	useEffect(() => {
		if (!hasFitted.current && locations.length > 0) {
			const bounds = L.latLngBounds(
				locations
					.filter(hasCoordinates)
					.map((loc) => [loc.latitude, loc.longitude]),
			);
			if (bounds.isValid()) {
				map.fitBounds(bounds, { padding: [50, 50] });
				hasFitted.current = true;
			}
		}
	}, [locations, map]);

	return null;
}

export default function MapView({
	selectedId,
	selectionRevision = 0,
	selectedIds,
	onSelect,
	mapTheme: controlledMapTheme,
	onMapThemeChange,
	geofences,
	geofencePanelOpen = false,
	isPickingFenceCenter = false,
	pickingGeofenceId,
	focusedGeofenceId,
	onFenceCenterSelected,
	onStopPickingFenceCenter,
	onToggleGeofencePanel,
	onStopFollow,
}: MapViewProps) {
  const { t } = useTranslation();
	const locations = useLocations();
	const { isRouteAllowed } = useContext(AppContext);
	const canReadSchoolTransportation =
		isRouteAllowed(Permissions.SCHOOL_TRANSPORTATION_READ) ||
		isRouteAllowed(Permissions.SCHOOL_TRANSPORTATION);
	const statusNow = useLocationStatusClock();
	const markerRefs = useRef(new Map<string, L.Marker>());
	const [expandedAttendanceVehicleId, setExpandedAttendanceVehicleId] =
		useState<string | null>(null);
	const [isFollowing, setIsFollowing] = useState(true);
	const [internalMapTheme, setInternalMapTheme] =
		useState<MapTheme>(getStoredMapTheme);
	const mapTheme = controlledMapTheme ?? internalMapTheme;
	const mapControlBackgroundColor =
		mapTheme === "dark"
			? "rgba(15, 23, 42, 0.92)"
			: "rgba(255, 255, 255, 0.94)";
	const mapControlHoverColor =
		mapTheme === "dark"
			? "rgba(30, 41, 59, 0.96)"
			: "rgba(241, 245, 249, 0.98)";
	const mapControlIconColor = mapTheme === "dark" ? "#ffffff" : "#334155";
	const mapControlBorderColor =
		mapTheme === "dark"
			? "rgba(148, 163, 184, 0.3)"
			: "rgba(100, 116, 139, 0.35)";
	const resolvedGeofences = geofences ?? [
		{ id: "default", enabled: false, center: null, radiusMeters: 300 },
	];

	useEffect(() => {
		if (!selectedId) return;

		setIsFollowing(true);
		setExpandedAttendanceVehicleId(null);
		const openPopupTimeout = window.setTimeout(
			() => markerRefs.current.get(selectedId)?.openPopup(),
			0,
		);

		return () => window.clearTimeout(openPopupTimeout);
	}, [selectedId, selectionRevision]);

	useEffect(() => {
		try {
			window.localStorage.setItem(MAP_THEME_STORAGE_KEY, mapTheme);
		} catch {
			// The map still works when browser storage is unavailable.
		}
	}, [mapTheme]);

	const handleMapThemeChange = (theme: MapTheme) => {
		setInternalMapTheme(theme);
		onMapThemeChange?.(theme);
	};

	const markersToRender = useMemo<MappableLocation[]>(() => {
		return locations
			.filter(
				(l) =>
					selectedIds.length === 0 ||
					selectedIds.includes(l.entityId) ||
					l.entityId === selectedId,
			)
			.filter(hasCoordinates);
	}, [locations, selectedId, selectedIds]);

	const selectedLocation = useMemo(() => {
		if (!selectedId) {
			return undefined;
		}

		const found = locations.find(
			(location) => location.entityId === selectedId,
		);
		return found && hasCoordinates(found) ? found : undefined;
	}, [locations, selectedId]);
	const {
		data: schoolTransportations = [],
		isLoading: isLoadingSchoolTransportations,
		isError: hasSchoolTransportationError,
	} =
		useGetAllSchoolTransportationByLicenseQuery(
			{ pageNumber: 1, pageSize: 9999 },
			{ name: "" },
			canReadSchoolTransportation &&
				selectedLocation?.source === "mobile-driver",
		);
	const selectedTransportation = useMemo(() => {
		const selectedPlate = normalizeVehiclePlate(selectedLocation?.vehiclePlate);
		if (!selectedPlate) return undefined;

		return schoolTransportations.find(
			(transportation) =>
				normalizeVehiclePlate(transportation.plate) === selectedPlate,
		);
	}, [schoolTransportations, selectedLocation?.vehiclePlate]);
	const shouldLoadSelectedAttendance =
		selectedLocation?.source === "mobile-driver" &&
		selectedLocation.isOnline === true &&
		Boolean(selectedTransportation);
	const {
		data: selectedAttendance,
		isLoading: isLoadingSelectedAttendance,
		isError: hasSelectedAttendanceError,
	} = useQuery(
		[
			SCHOOL_TRANSPORTATION_KEYS.ALL,
			"activeAttendance",
			selectedTransportation?.id,
		],
		() =>
			getActiveSchoolTransportationAttendanceAsync(
				selectedTransportation!.id,
			),
		{
			enabled: shouldLoadSelectedAttendance,
			refetchInterval: shouldLoadSelectedAttendance ? 5000 : false,
			refetchOnWindowFocus: true,
			retry: false,
		},
	);
	const selectedPresentStudents = useMemo(
		() =>
			(selectedAttendance?.students ?? [])
				.filter((student) => student.status === "Present")
				.sort((left, right) =>
					left.name.localeCompare(right.name, getLocationLocale(), {
						sensitivity: "base",
					}),
				),
		[selectedAttendance?.students, t],
	);
	const isSelectedAttendanceLoading =
		isLoadingSchoolTransportations || isLoadingSelectedAttendance;
	const isSelectedAttendanceUnavailable =
		hasSchoolTransportationError ||
		hasSelectedAttendanceError ||
		(!isLoadingSchoolTransportations && !selectedTransportation);

	const focusedGeofence = useMemo(() => {
		if (!focusedGeofenceId) {
			return null;
		}

		return (
			resolvedGeofences.find((geofence) => geofence.id === focusedGeofenceId) ??
			null
		);
	}, [focusedGeofenceId, resolvedGeofences]);

	return (
		<MapContainer
			center={[0, 0]}
			zoom={2}
			className="map-container"
			style={{ height: "100%", width: "100%" }}
		>
			<MapThemeController theme={mapTheme} />
			<TileLayer
				url={OPEN_STREET_MAP_TILE_URL}
				attribution={OPEN_STREET_MAP_ATTRIBUTION}
			/>

			{resolvedGeofences.map((g) => (
				<GeofenceOverlay key={g.id} geofence={g} />
			))}

			<MapClickController
				isPickingFenceCenter={isPickingFenceCenter}
				pickingGeofenceId={pickingGeofenceId}
				onFenceCenterSelected={onFenceCenterSelected ?? (() => {})}
				onStopPickingFenceCenter={onStopPickingFenceCenter ?? (() => {})}
			/>

			{resolvedGeofences.map((g) => (
				<GeofenceNotificationController
					key={g.id}
					geofence={g}
					locations={locations}
				/>
			))}

			<IconButton
				onClick={onToggleGeofencePanel}
				sx={{
					position: "absolute",
					top: 12,
					right: 12,
					zIndex: 1200,
					backgroundColor: mapControlBackgroundColor,
					color: mapControlIconColor,
					border: `1px solid ${mapControlBorderColor}`,
					boxShadow: 3,
					borderRadius: "50%",
					width: 48,
					height: 48,
					"&:hover": {
						backgroundColor: mapControlHoverColor,
					},
				}}
				title={
					geofencePanelOpen ? t("map.closeVirtualFence") : t("map.openVirtualFence")
				}
			>
				{geofencePanelOpen ? (
					<CloseFullscreenIcon />
				) : (
					<SettingsInputAntennaIcon />
				)}
			</IconButton>

			<MuiTooltip
				title={
					mapTheme === "dark"
						? t("map.useLightMap")
						: t("map.useDarkMap")
					}
			>
				<Box
					component="button"
					type="button"
					role="switch"
					aria-checked={mapTheme === "dark"}
					onClick={() =>
						handleMapThemeChange(mapTheme === "dark" ? "light" : "dark")
					}
					aria-label={
						mapTheme === "dark"
							? t("map.useLightMap")
							: t("map.useDarkMap")
					}
					sx={{
						position: "absolute",
						top: 17,
						right: 68,
						zIndex: 1200,
						width: 72,
						height: 38,
						p: 0,
						border: "1px solid rgba(148, 163, 184, 0.7)",
						borderRadius: "19px",
						backgroundColor:
							mapTheme === "dark"
								? "rgba(15, 23, 42, 0.96)"
								: "rgba(241, 245, 249, 0.96)",
						boxShadow: 3,
						cursor: "pointer",
						transition:
							"background-color 180ms ease, border-color 180ms ease",
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						overflow: "hidden",
						"&:hover": {
							borderColor:
								mapTheme === "dark"
									? "rgba(147, 197, 253, 0.95)"
									: "rgba(71, 85, 105, 0.95)",
						},
						"&:focus-visible": {
							outline: "3px solid rgba(59, 130, 246, 0.45)",
							outlineOffset: 2,
						},
					}}
				>
					<Box
						sx={{
							position: "absolute",
							top: 3,
							left: 3,
							width: 30,
							height: 30,
							borderRadius: "50%",
							backgroundColor:
								mapTheme === "dark" ? "#334155" : "#ffffff",
							boxShadow: "0 2px 8px rgba(15, 23, 42, 0.35)",
							transform:
								mapTheme === "dark"
									? "translateX(34px)"
									: "translateX(0)",
							transition:
								"transform 180ms ease, background-color 180ms ease",
						}}
					/>
					<LightModeIcon
						sx={{
							position: "relative",
							zIndex: 1,
							ml: "7px",
							fontSize: 20,
							color: mapTheme === "light" ? "#f59e0b" : "#94a3b8",
							transition: "color 180ms ease",
						}}
					/>
					<DarkModeIcon
						sx={{
							position: "relative",
							zIndex: 1,
							mr: "7px",
							fontSize: 19,
							color: mapTheme === "dark" ? "#bfdbfe" : "#64748b",
							transition: "color 180ms ease",
						}}
					/>
				</Box>
			</MuiTooltip>

			{markersToRender.map((loc) => {
				const plateLabel = formatPlateDisplay(loc.vehiclePlate || loc.entityId);
				const addressLines = splitAddressAfterCity(loc.address);
				const operationalStatus = getOperationalStatus(loc);
				const telemetryStatus = getLocationTelemetryStatus(loc, statusNow);
				const telemetryPresentation = getTelemetryPresentation(telemetryStatus);
				const telemetryPopupLabel =
					telemetryStatus === "recent"
						? t("locationTransport.gpsRecent")
						: telemetryStatus === "delayed"
							? t("locationTransport.gpsDelayed")
							: telemetryStatus === "stale"
								? t("locationTransport.gpsStale")
								: t("locationTransport.noGps");
				const isSelected = selectedId === loc.entityId;
				const canShowAttendance =
					isSelected &&
					loc.source === "mobile-driver" &&
					loc.isOnline === true &&
					canReadSchoolTransportation;
				const isAttendanceExpanded =
					expandedAttendanceVehicleId === loc.entityId;
				const shouldShowDriverPhoto =
					loc.source === "mobile-driver" && !!loc.driverPhotoUrl;

				return (
					<Fragment key={loc.entityId}>
						{isSelected && (
							<CircleMarker
								center={[loc.latitude, loc.longitude]}
								radius={24}
								pathOptions={{
									color: "#38bdf8",
									fillColor: "#38bdf8",
									fillOpacity: 0.12,
									weight: 3,
								}}
								className="vehicle-marker-selection-ring"
								interactive={false}
							/>
						)}
						<DriftMarker
							key={`vehicle-${loc.entityId}`}
							ref={(marker) => {
								if (marker) markerRefs.current.set(loc.entityId, marker);
								else markerRefs.current.delete(loc.entityId);
							}}
							position={[loc.latitude, loc.longitude]}
							duration={1000}
							icon={getVehicleIcon(loc)}
							eventHandlers={{
								click: () => onSelect(loc.entityId),
							}}
						>
							<Popup
								className="vehicle-map-popup"
								autoClose={false}
								closeButton={true}
								autoPan={false}
								keepInView={false}
							>
								<div
									style={{
										fontFamily: "Inter, Roboto, sans-serif",
										fontSize: "11px",
										lineHeight: 1.3,
										padding: "2px 4px",
									}}
									className="vehicle-map-popup__content"
								>
									{loc.source === "mobile-driver" && !!loc.driverName && (
										<div className="vehicle-map-popup__driver-info">
											<DriverInfoPopover
												name={loc.driverName}
												cpf={loc.driverCpf}
												phone={loc.driverPhone}
												email={loc.driverEmail}
											/>
										</div>
									)}
									<div className="vehicle-map-popup__header">
										{shouldShowDriverPhoto && (
											<Avatar
												src={loc.driverPhotoUrl}
												alt={t("locationTransport.driverIdentifier", { plate: plateLabel })}
												className="vehicle-map-popup__driver-photo"
												sx={{
													width: 48,
													height: 48,
													bgcolor: "rgba(37, 99, 235, 0.15)",
													color: "#60a5fa",
													fontSize: 16,
													fontWeight: 700,
												}}
											>
												{plateLabel.charAt(0)}
											</Avatar>
										)}
										<div className="vehicle-map-popup__identity">
											<div className="vehicle-map-popup__plate">{plateLabel}</div>
											{loc.source === "mobile-driver" && !!loc.driverName && (
												<div className="vehicle-map-popup__secondary">
													{loc.driverName}
												</div>
											)}
											{!!loc.modelName && (
												<div className="vehicle-map-popup__secondary">
													{loc.modelName}
												</div>
											)}
										</div>
									</div>
									<div className="vehicle-map-popup__statuses">
										<span className={`vehicle-map-popup__status vehicle-map-popup__status--${operationalStatus.color}`}>
											{operationalStatus.label}
										</span>
										<span className={`vehicle-map-popup__status vehicle-map-popup__status--${telemetryPresentation.color}`}>
											{telemetryPopupLabel}
										</span>
									</div>
									<div className="vehicle-map-popup__secondary">
										{formatLastLocationSignal(loc.timestamp, statusNow)}
									</div>
									{canShowAttendance && (
										<div className="vehicle-map-popup__attendance">
											<button
												type="button"
												className="vehicle-map-popup__attendance-toggle"
										disabled={
											isSelectedAttendanceLoading ||
											isSelectedAttendanceUnavailable ||
											selectedPresentStudents.length === 0
												}
												aria-expanded={isAttendanceExpanded}
												onClick={(event) => {
													event.stopPropagation();
													setExpandedAttendanceVehicleId((current) =>
														current === loc.entityId ? null : loc.entityId,
													);
												}}
											>
												<span>
											{isSelectedAttendanceLoading
												? t("locationTransport.loadingPresentStudents")
												: isSelectedAttendanceUnavailable
													? t("locationTransport.attendanceUnavailable")
												: selectedPresentStudents.length === 0
															? t("locationTransport.noPresentStudents")
															: t("locationTransport.presentStudentCount", { count: selectedPresentStudents.length })}
												</span>
												{selectedPresentStudents.length > 0 && (
													<span aria-hidden="true">
														{isAttendanceExpanded ? "▴" : "▾"}
													</span>
												)}
											</button>
											{isAttendanceExpanded && (
												<div
													className="vehicle-map-popup__attendance-list"
													onClick={(event) => event.stopPropagation()}
													onWheel={(event) => event.stopPropagation()}
												>
													{selectedPresentStudents.map((student) => (
														<div
															key={student.studentId}
															className="vehicle-map-popup__attendance-student"
														>
															<Avatar
																src={student.image || undefined}
																alt={student.name}
																sx={{ width: 24, height: 24, fontSize: 10 }}
															>
																{student.name.charAt(0).toUpperCase()}
															</Avatar>
															<span>{student.name}</span>
														</div>
													))}
												</div>
											)}
										</div>
									)}
									<div className="vehicle-map-popup__address">
										<LocationOnOutlinedIcon sx={{ fontSize: 15 }} />
										<div>
											<div>{addressLines.firstLine}</div>
											{addressLines.secondLine && (
												<div>{addressLines.secondLine}</div>
											)}
										</div>
									</div>
								</div>
							</Popup>
						</DriftMarker>

						<Marker
							key={`plate-${loc.entityId}`}
							position={[loc.latitude, loc.longitude]}
							icon={createPlateIcon(plateLabel)}
							interactive={false}
							keyboard={false}
							zIndexOffset={-1000}
						/>
					</Fragment>
				);
			})}

			<InitialBoundsController locations={markersToRender} />

			<MapController
				selectedLocation={selectedLocation}
				isFollowing={isFollowing}
			/>
			{focusedGeofence?.center && (
				<FocusedGeofenceController geofence={focusedGeofence} />
			)}
			<ResizeController trigger={geofencePanelOpen} />

			{selectedId && isFollowing && (
				<MuiTooltip title={t("map.stopFollow")}>
					<IconButton
						onClick={() => {
							setIsFollowing(false);
							onStopFollow?.();
						}}
						sx={{
							position: "absolute",
							top: 90,
							left: 5,
							zIndex: 1000,
							backgroundColor: mapControlBackgroundColor,
							color: mapControlIconColor,
							border: `1px solid ${mapControlBorderColor}`,
							boxShadow: 3,
							borderRadius: "50%",
							width: 48,
							height: 48,
							"&:hover": {
								backgroundColor: mapControlHoverColor,
								opacity: 1,
							},
						}}
					>
						<StopIcon />
					</IconButton>
				</MuiTooltip>
			)}
		</MapContainer>
	);
}
